// ============================================================
//  src/admin/pages/AdminBotDetailPage.js
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getBot, botAction } from '../api/adminClient';
import { Badge, isFlagTrue } from '../components/UI';
import { useConfirm } from '../components/ConfirmModal';

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d.replace(' ', 'T'));
  return isNaN(dt.getTime()) ? '—' : dt.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function AdminBotDetailPage() {
  const { bId } = useParams();
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    getBot(bId).then(res => setData(res.data)).catch(() => toast.error('Could not load bot')).finally(() => setLoading(false));
  }, [bId]);

  useEffect(() => { load(); }, [load]);

  async function handleSuspend() {
    if (!reason.trim()) { toast.error('Please provide a reason for the record'); return; }
    const ok = await confirm({
      title: 'Suspend this bot?',
      message: 'It will stop responding to all WhatsApp messages immediately.',
      confirmText: 'Suspend Bot',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await botAction({ action: 'suspend', bId, reason: reason.trim() });
      toast.success('Bot suspended');
      setReason('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Action failed');
    } finally { setBusy(false); }
  }

  async function handleUnsuspend() {
    setBusy(true);
    try {
      await botAction({ action: 'unsuspend', bId });
      toast.success('Bot unsuspended');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Action failed');
    } finally { setBusy(false); }
  }

  async function handleQueueMode(mode) {
    setBusy(true);
    try {
      await botAction({ action: 'set_queue_mode', bId, mode });
      toast.success('Queue mode updated');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Action failed');
    } finally { setBusy(false); }
  }

  if (loading) return <div className="bf-empty"><span className="spinner spinner-dark" /> Loading bot…</div>;
  if (!data) return <div className="bf-empty">Bot not found.</div>;

  const { bot, stats, totalCommands, totalFlows, recent_logs } = data;

  return (
    <div className="bf-page">
      <Link to="/bot-father/bots" className="bf-back-link">← Back to Bots</Link>

      <div className="bf-detail-header">
        <div>
          <div className="bf-detail-name">{bot.bName}</div>
          <div className="bf-detail-meta bf-mono">{bot.bId}</div>
          <div className="bf-detail-meta">Owner: {bot.sFname} {bot.sLname} ({bot.sEmail})</div>
        </div>
        {isFlagTrue(bot.bAdminSuspended) ? <Badge tone="danger">Suspended</Badge> : <Badge tone="success">Active</Badge>}
      </div>

      {/* Suspend / unsuspend */}
      <div className="bf-card">
        <div className="bf-card-hdr">Service Control</div>
        <div className="bf-card-body">
          {isFlagTrue(bot.bAdminSuspended) ? (
            <div>
              <div style={{ fontSize: 13.5, marginBottom: 12 }}>
                This bot has been suspended by an admin and will not respond to messages.
                {bot.bSuspendReason && <div className="bf-muted" style={{ marginTop: 4 }}>Reason: {bot.bSuspendReason}</div>}
              </div>
              <button className="btn btn-primary" disabled={busy} onClick={handleUnsuspend}>Unsuspend Bot</button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <input
                className="bf-input"
                placeholder="Reason for suspension (visible to internal logs)"
                value={reason}
                onChange={e => setReason(e.target.value)}
                style={{ flex: 1, minWidth: 220, width: 'auto' }}
              />
              <button className="bf-btn-danger-outline" disabled={busy} onClick={handleSuspend}>
                Suspend Bot
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Queue mode override */}
      <div className="bf-card">
        <div className="bf-card-hdr">Queue Mode</div>
        <div className="bf-card-body">
          <div style={{ fontSize: 13.5, marginBottom: 12 }}>
            Controls whether this bot's incoming messages are processed instantly (sync) or
            handed to the background worker (queued) under load. "Auto" follows the
            platform-wide default set on the Bots page.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['auto', 'on', 'off'].map(mode => {
              const current =
                bot.bQueueMode === 1 || bot.bQueueMode === '1' ? 'on' :
                bot.bQueueMode === 0 || bot.bQueueMode === '0' ? 'off' : 'auto';
              const isCurrent = current === mode;
              return (
                <button
                  key={mode}
                  className={isCurrent ? 'btn btn-primary' : 'btn btn-outline'}
                  disabled={busy || isCurrent}
                  onClick={() => handleQueueMode(mode)}
                >
                  {mode === 'auto' ? 'Auto (platform default)' : mode === 'on' ? 'Force On' : 'Force Off'}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="bf-card">
        <div className="bf-card-hdr">Stats</div>
        <div className="bf-card-body bf-stat-grid" style={{ marginBottom: 0 }}>
          <MiniStat label="Total Users" value={stats.totalUsers || 0} />
          <MiniStat label="Blocked Users" value={stats.blockedUsers || 0} />
          <MiniStat label="Total Messages" value={stats.totalMessages || 0} />
          <MiniStat label="New Today" value={stats.newToday || 0} />
          <MiniStat label="Commands" value={totalCommands} />
          <MiniStat label="Flows" value={totalFlows} />
        </div>
      </div>

      {/* Bot details (masked credentials) */}
      <div className="bf-card">
        <div className="bf-card-hdr">Bot Details</div>
        <div className="bf-card-body" style={{ padding: '4px 20px' }}>
          <Row label="Phone Number ID" value={bot.bPhoneNumberId || '—'} />
          <Row label="WABA ID" value={bot.bWabaId || '—'} />
          <Row label="Access Token" value={bot.bToken || '—'} />
          <Row label="App Secret" value={bot.bAppSecret || '—'} />
          <Row label="Send Key" value={bot.bSendKey || '—'} />
          <Row label="Created" value={fmtDate(bot.bCreatedAt)} />
        </div>
      </div>

      {/* Recent logs */}
      <div className="bf-card">
        <div className="bf-card-hdr">Recent Logs</div>
        <div className="bf-card-body-flush">
          {recent_logs.length === 0 ? (
            <div className="bf-empty">No logs yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {recent_logs.map(l => (
                <div key={l.lId} style={{ padding: '9px 18px', borderBottom: '1px solid var(--bf-border)', fontSize: 12.5, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{
                    fontWeight: 700, textTransform: 'uppercase', fontSize: 10.5, flexShrink: 0,
                    color: l.lLevel === 'error' ? 'var(--bf-danger)' : l.lLevel === 'warn' ? 'var(--bf-warning)' : 'var(--bf-text-muted)',
                  }}>{l.lLevel}</span>
                  <span style={{ flex: 1, minWidth: 140 }}>{l.lMessage}</span>
                  <span className="bf-muted" style={{ flexShrink: 0 }}>{fmtDate(l.lCreatedAt)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 20, fontWeight: 800 }}>{value}</div>
      <div className="bf-stat-label" style={{ marginTop: 3 }}>{label}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="bf-kv">
      <span className="bf-kv-key">{label}</span>
      <span className="bf-kv-val">{value}</span>
    </div>
  );
}